/**
 * "This browser agreed to be recorded, to this wording."
 *
 * `localStorage`, like the tutorial flag: there are no accounts, so a browser
 * profile is the player. The record is what the server is shown on `joinRoom`,
 * and the server is the one that decides whether it counts — this file only
 * remembers the answer so a returning player is not asked again for the same
 * wording.
 *
 * A record for any other `CONSENT_VERSION` reads as no record: the wording has
 * changed since, and the player has not agreed to the new one.
 *
 * Every access is wrapped because a locked-down profile throws on the property
 * itself. Failing to read is failing to find consent, which asks again — the
 * safe side to fail on.
 */

import type { ConsentRecord } from '@overcooked/shared'
import { CONSENT_VERSION } from '@overcooked/shared'

export const CONSENT_KEY = 'overcooked.consent'

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function browserStore(): Store | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** The stored agreement to the current wording, or `null` if there is none. */
export function storedConsent(store: Store | null = browserStore()): ConsentRecord | null {
  try {
    const raw = store?.getItem(CONSENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ConsentRecord> | null
    if (!parsed || typeof parsed !== 'object') return null
    if (parsed.version !== CONSENT_VERSION) return null
    if (typeof parsed.acceptedAt !== 'string' || !Number.isFinite(Date.parse(parsed.acceptedAt))) return null
    return { version: parsed.version, acceptedAt: parsed.acceptedAt }
  } catch {
    return null
  }
}

/** Remember that the player agreed just now. Returns the record either way. */
export function saveConsent(now: Date = new Date(), store: Store | null = browserStore()): ConsentRecord {
  const record: ConsentRecord = { version: CONSENT_VERSION, acceptedAt: now.toISOString() }
  try {
    store?.setItem(CONSENT_KEY, JSON.stringify(record))
  } catch {
    // Not remembered, so the player is asked again next time. The record is
    // still returned, so this visit can go on.
  }
  return record
}

/** Forget the stored agreement — the server refused it, so ask again. */
export function clearConsent(store: Store | null = browserStore()): void {
  try {
    store?.removeItem(CONSENT_KEY)
  } catch {
    // Nothing to do: an unreadable store already reads as no consent.
  }
}

/**
 * The first gate once the room's seat count is known.
 *
 * `null` is a code no room has been opened for yet. The join creates it at the
 * box's default size, which is a room for two, so it is treated as one.
 */
export function firstStage(roomSeats: number | null, consented: boolean): 'consent' | 'mic' | 'latency' {
  if (roomSeats === 1) return 'latency'
  return consented ? 'mic' : 'consent'
}

/**
 * Where each gate sits in the "Step N of M" it shows. A gate this room skips
 * is numbered 0 and never drawn.
 */
export function gateSteps(
  solo: boolean,
  askedConsent: boolean,
): { consent: number; mic: number; latency: number; of: number } {
  if (solo) return { consent: 0, mic: 0, latency: 1, of: 1 }
  if (askedConsent) return { consent: 1, mic: 2, latency: 3, of: 3 }
  return { consent: 0, mic: 1, latency: 2, of: 2 }
}
